import { Fragment, useCallback, useState } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import CreateBlogModal from "./createBlogModal";
import EditBlogModal from "./editBlogModal";
import { useDebounce } from "../../../Hooks/useDebounce";
import { getAllBlogs, deleteBlogs } from "../../../store/slices/blogsSlice";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Button, IconButton, Tooltip } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";

const Blogs = () => {
  const dispatch = useDispatch();
  const client = useSelector((state) => state.clients?.selectedClient?.value);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isEditOpen, setEditOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = async () => {
    const sortField = sorting.length > 0 ? sorting[0].id : "";
    const sortOrder = sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "";
    const queryPayload = {
      limit: pageSize,
      offset: page * pageSize,
      sortBy: sortField,
      sortOrder: sortOrder,
      search: debouncedSearch,
    };
    try {
      const response = await dispatch(getAllBlogs(queryPayload)).unwrap();
      setData(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, sorting, debouncedSearch, isCreateOpen, isEditOpen]);

  const handlePageChange = (event) => {
    const newState = typeof event === "function" ? event({ pageIndex: page, pageSize }) : event;
    setPage(newState.pageIndex);
    setPageSize(newState.pageSize);
  };

  const handleEditClick = (e, row) => {
    e.stopPropagation();
    setSelectedBlog(row.original);
    setEditOpen(true);
  };

  const handleDeleteClick = async (e, row) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Are you sure you want to delete "${row.original.title}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const result = await dispatch(deleteBlogs({ id: row.original.id }));
      if (result?.payload) {
        toast.success("Blog deleted successfully!");
        fetchData();
      } else {
        toast.error("Failed to delete blog.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while deleting.");
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 70 },
      { accessorKey: "title", header: "Title", size: 250 },
      { accessorKey: "category", header: "Category", size: 150 },
      { accessorKey: "status", header: "Status", size: 100 },
      {
        accessorKey: "blog_date",
        header: "Blog Date",
        size: 130,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "-",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 130,
        Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "Actions",
        size: 100,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 4 }}>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={(e) => handleEditClick(e, row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount || 0,
    onPaginationChange: handlePageChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination: { pageIndex: page, pageSize },
      sorting,
      columnFilters,
      globalFilter: searchQuery,
    },
    globalFilter: searchQuery,
    onGlobalFilterChange: setSearchQuery,
    initialState: { density: "compact" },
    renderTopToolbarCustomActions: () => (
      <div className="d-flex">
        <button
          className="btn bg-primary"
          disabled={client === "ts"}
          onClick={() => setCreateOpen(true)}
        >
          + Create Blog
        </button>
      </div>
    ),
  });

  return (
    <Fragment>
      <CreateBlogModal isOpen={isCreateOpen} toggle={() => setCreateOpen(false)} />
      <EditBlogModal isOpen={isEditOpen} toggle={() => setEditOpen(false)} blog={selectedBlog} />
      <Breadcrumbs mainTitle="Blogs" parent="Blogs" parentPath="blogs" title="Blogs List" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Blogs;
